import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import './QuizGame.css';
import './KBCMultiplex.css';

const KBCMultiplexMultiplayer = ({ 
  gameSession, 
  userRole, 
  userName, 
  userClass, 
  onExit 
}) => {
  console.log('KBCMultiplexMultiplayer received gameSession:', gameSession);
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState('waiting');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [scores, setScores] = useState({});
  const [opponentData, setOpponentData] = useState({});
  const [lifelines, setLifelines] = useState({
    fiftyFifty: true,
    skip: true,
    hint: true
  });

  const prizeAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 500000, 1000000];

  // Audio management
  const [currentAudio, setCurrentAudio] = useState(null);
  const [timerAudio, setTimerAudio] = useState(null);

  const playAudio = (audioFile) => {
    try {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      const audio = new Audio(`https://therisingschool.com/Learning%20Games/MP3/${audioFile}`);
      audio.volume = 0.5;
      setCurrentAudio(audio);
      
      audio.play().catch(error => console.log('Audio play failed:', error));
      audio.onended = () => setCurrentAudio(null);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  };

  const startTimerAudio = () => {
    try {
      if (timerAudio) {
        timerAudio.pause();
        timerAudio.currentTime = 0;
      }
      
      const audio = new Audio('https://therisingschool.com/Learning%20Games/MP3/Timer.mp3');
      audio.volume = 0.3;
      audio.loop = true;
      setTimerAudio(audio);
      
      audio.play().catch(error => console.log('Timer audio play failed:', error));
    } catch (error) {
      console.log('Timer audio not supported:', error);
    }
  };

  const stopTimerAudio = () => {
    if (timerAudio) {
      timerAudio.pause();
      timerAudio.currentTime = 0;
      setTimerAudio(null);
    }
  };

  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    stopTimerAudio();
  };

  // Initialize socket connection
  useEffect(() => {
    const gameId = gameSession?.id || gameSession?.gameSession?.id;
    if (!gameId) return;
    
    const newSocket = io('http://localhost:5000');
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      newSocket.emit('join-game', gameId);
    });

    newSocket.on('opponent-score-update', (data) => {
      setOpponentData(prev => ({
        ...prev,
        score: data.score,
        questionIndex: data.questionIndex,
        isCorrect: data.isCorrect
      }));
      
      setScores(prev => ({
        ...prev,
        [data.playerName]: data.score
      }));
    });

    newSocket.on('game-state-updated', (data) => {
      setGameState(data.state);
      if (data.scores) setScores(data.scores);
    });

    return () => {
      if (gameId) {
        newSocket.emit('leave-game', gameId);
      }
      newSocket.disconnect();
    };
  }, [gameSession]);

  // Load questions
  useEffect(() => {
    console.log('Loading questions from gameSession:', gameSession);
    const questions = gameSession?.questions || gameSession?.gameSession?.questions;
    const players = gameSession?.players || gameSession?.gameSession?.players || [];
    
    if (questions && questions.length >= 12) {
      setQuestions(questions.slice(0, 12));
      setGameState('playing');
      
      const initialScores = {};
      players.forEach(player => {
        initialScores[player.name] = 0;
      });
      setScores(initialScores);
      console.log('Game initialized with', questions.length, 'questions and', players.length, 'players');
    } else {
      console.log('Insufficient questions or players:', { questionsCount: questions?.length, playersCount: players.length });
    }
  }, [gameSession]);

  // Play question sound when new question starts
  useEffect(() => {
    if (gameState === 'playing' && questions.length > 0 && !showResult) {
      setTimeout(() => {
        playAudio('Question.mp3');
        setTimeout(() => startTimerAudio(), 1000);
      }, 100);
    }
  }, [currentQuestion, gameState, showResult]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const submitAnswer = () => {
    stopTimerAudio();
    
    const correct = selectedAnswer === questions[currentQuestion].correctAnswer;
    const points = correct ? prizeAmounts[currentQuestion] : 0;
    
    const newScores = { ...scores };
    newScores[userName] = (newScores[userName] || 0) + points;
    setScores(newScores);

    // Emit score update to opponent
    if (socket) {
      const gameId = gameSession?.id || gameSession?.gameSession?.id;
      socket.emit('score-update', {
        gameId: gameId,
        playerName: userName,
        score: newScores[userName],
        questionIndex: currentQuestion,
        isCorrect: correct
      });
    }

    setShowResult(true);
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer('');
        setShowResult(false);
      } else {
        endGame();
      }
    }, 2000);
  };

  const endGame = () => {
    setGameState('finished');
    stopAudio();
    if (socket) {
      const gameId = gameSession?.id || gameSession?.gameSession?.id;
      socket.emit('game-state-change', {
        gameId: gameId,
        state: 'finished',
        scores
      });
    }
  };

  const useFiftyFifty = () => {
    if (!lifelines.fiftyFifty) return;
    
    playAudio('Question.mp3');
    
    const currentQ = questions[currentQuestion];
    const wrongAnswers = currentQ.options.filter(opt => opt !== currentQ.correctAnswer);
    const toRemove = wrongAnswers.slice(0, 2);
    
    setQuestions(prev => {
      const updated = [...prev];
      updated[currentQuestion] = {
        ...updated[currentQuestion],
        options: updated[currentQuestion].options.filter(opt => !toRemove.includes(opt))
      };
      return updated;
    });
    
    setLifelines(prev => ({ ...prev, fiftyFifty: false }));
  };

  const useSkip = () => {
    if (!lifelines.skip || currentQuestion >= questions.length - 1) return;
    
    playAudio('Question.mp3');
    
    setCurrentQuestion(currentQuestion + 1);
    setSelectedAnswer('');
    setLifelines(prev => ({ ...prev, skip: false }));
  };

  const useHint = () => {
    if (!lifelines.hint) return;
    
    playAudio('Question.mp3');
    alert(questions[currentQuestion].hint || "No hint available for this question");
    setLifelines(prev => ({ ...prev, hint: false }));
  };

  if (gameState === 'waiting') {
    return (
      <div className="quiz-game kbc-multiplex-mode">
        <div className="prize-ladder-container">
          <h2>🎮 Multiplayer KBC Multiplex</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div>Waiting for both players...</div>
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              Players: {(gameSession?.players || gameSession?.gameSession?.players || []).map(p => p.name).join(' vs ')}
            </div>
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#888' }}>
              12 questions • No time limit • Timer sounds for ambiance
            </div>
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="quit-btn">Exit Game</button>
        </div>
      </div>
    );
  }

  if (gameState === 'finished') {
    const winner = Object.keys(scores).reduce((a, b) => scores[a] > scores[b] ? a : b);
    
    return (
      <div className="quiz-game kbc-multiplex-mode">
        <div className="prize-ladder-container">
          <h2>🏆 Multiplex Game Over!</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <h3>Final Scores:</h3>
            {Object.entries(scores).map(([player, score]) => (
              <div key={player} style={{ 
                margin: '10px 0', 
                fontSize: '18px',
                fontWeight: player === winner ? 'bold' : 'normal',
                color: player === winner ? '#10b981' : '#000'
              }}>
                {player}: {score.toLocaleString()} Points
                {player === winner && ' 🏆'}
              </div>
            ))}
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="continue-btn">Back to Games</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  if (!currentQ) return <div>Loading...</div>;

  return (
    <div className="quiz-game kbc-multiplex-mode">
      {/* Players Dashboard */}
      <div className="players-dashboard" style={{
        position: 'fixed',
        top: window.innerWidth < 768 ? 'auto' : '10px',
        bottom: window.innerWidth < 768 ? '10px' : 'auto',
        right: window.innerWidth < 768 ? '5px' : '10px',
        left: window.innerWidth < 768 ? '5px' : 'auto',
        background: 'rgba(0,0,0,0.95)',
        color: 'white',
        padding: window.innerWidth < 768 ? '8px' : '15px',
        borderRadius: '8px',
        fontSize: window.innerWidth < 768 ? '10px' : '14px',
        zIndex: 1000,
        width: window.innerWidth < 768 ? 'auto' : '250px',
        border: '2px solid #8b5cf6'
      }}>
        <div style={{ 
          fontWeight: 'bold', 
          marginBottom: '10px', 
          color: '#8b5cf6', 
          textAlign: 'center'
        }}>🏆 Multiplex Scores</div>
        
        <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(139, 92, 246, 0.2)', borderRadius: '5px' }}>
          <div style={{ fontSize: '12px', color: '#8b5cf6' }}>YOU</div>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>👤 {userName}</div>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#8b5cf6' }}>💰 {scores[userName] || 0}</div>
          <div style={{ fontSize: '12px' }}>📝 Q: {currentQuestion + 1}/12</div>
        </div>
        
        <div style={{ padding: '8px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '5px' }}>
          <div style={{ fontSize: '12px', color: '#ef4444' }}>OPPONENT</div>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>👤 {(gameSession?.players || gameSession?.gameSession?.players || []).find(p => p.name !== userName)?.name || 'Unknown'}</div>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ef4444' }}>💰 {scores[(gameSession?.players || gameSession?.gameSession?.players || []).find(p => p.name !== userName)?.name] || 0}</div>
          <div style={{ fontSize: '12px' }}>📝 Q: {(opponentData.questionIndex || 0) + 1}/12</div>
        </div>
      </div>

      <div className="game-header">
        <div className="points-display">
          <div className="coin-animation">
            🪙 {scores[userName] || 0} Points
          </div>
        </div>
        
        <div className="timer-circle multiplex-timer-circle">
          <div className="timer-inner multiplex-timer-inner">
            <span>∞</span>
          </div>
        </div>
        
        <div className="question-counter">
          {currentQuestion + 1}/12
        </div>
      </div>

      <div className="question-section">
        <div className="question-box multiplex-question-box">
          <div className="multiplex-mode-indicator">
            <span className="multiplex-mode-badge">🎯 Multiplex Multiplayer</span>
            <span className="multiplex-custom-badge">⚡ NO TIME LIMIT</span>
          </div>
          <h3>{currentQ.question}</h3>
        </div>
        
        <div className="options-grid">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              className={`option-btn ${selectedAnswer === option ? 'selected' : ''} ${
                showResult ? (option === currentQ.correctAnswer ? 'correct' : 
                selectedAnswer === option ? 'wrong' : '') : ''
              }`}
              onClick={() => handleAnswerSelect(option)}
              disabled={showResult}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="game-controls">
        <div className="lifelines">
          <button 
            className={`lifeline-btn ${!lifelines.fiftyFifty ? 'used' : ''}`}
            onClick={useFiftyFifty}
            disabled={!lifelines.fiftyFifty}
          >
            50:50
          </button>
          <button 
            className={`lifeline-btn ${!lifelines.skip ? 'used' : ''}`}
            onClick={useSkip}
            disabled={!lifelines.skip}
          >
            Skip
          </button>
          <button 
            className={`lifeline-btn ${!lifelines.hint ? 'used' : ''}`}
            onClick={useHint}
            disabled={!lifelines.hint}
          >
            Hint
          </button>
        </div>
        
        <div className="action-buttons">
          <button 
            className="submit-btn"
            onClick={submitAnswer}
            disabled={!selectedAnswer || showResult}
          >
            Final Answer
          </button>
          <button className="quit-btn" onClick={() => { stopAudio(); onExit(); }}>Quit Game</button>
        </div>
      </div>
    </div>
  );
};

export default KBCMultiplexMultiplayer;